'use client'

import { useState, useMemo } from 'react'
import { cn } from '@paitox/ui/lib/utils'
import { Search, Plus, Building2, Calendar, CheckCircle2, Clock } from 'lucide-react'
import { db, Room as DbRoom } from '../lib/db'

export function RoomsPage() {
  const [rooms, setRooms] = useState<DbRoom[]>(() => db.getRooms())
  const [search, setSearch] = useState('')
  const [showNew, setShowNew] = useState(false)

  // Creation fields
  const [name, setName] = useState('')
  const [specialty, setSpecialty] = useState('facial')
  const [capacity, setCapacity] = useState(1)

  const filtered = useMemo(() => {
    if (!search) return rooms
    return rooms.filter(r => r.name.toLowerCase().includes(search.toLowerCase()) || r.specialty.toLowerCase().includes(search.toLowerCase()))
  }, [rooms, search])

  const handleCreate = () => {
    if (!name) return

    const newRoom: DbRoom = {
      id: `r-${Date.now()}`,
      name,
      specialty,
      capacity,
      position: rooms.length,
      isActive: true,
      schedule: Array.from({ length: 7 }, (_, i) => ({
        dayOfWeek: i,
        openTime: '09:00',
        closeTime: '20:00',
        isClosed: i === 0 // Sunday closed by default
      }))
    }

    const updated = [...rooms, newRoom]
    setRooms(updated)
    db.saveRooms(updated)
    setShowNew(false)

    setName('')
    setSpecialty('facial')
    setCapacity(1)
  }

  const toggleActive = (id: string) => {
    const updated = rooms.map(r => r.id === id ? { ...r, isActive: !r.isActive } : r)
    setRooms(updated)
    db.saveRooms(updated)
  }

  const getWeekdaysShort = (room: DbRoom) => {
    const names = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    return room.schedule.map(s => (
      <span key={s.dayOfWeek} className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded mr-1 inline-block", s.isClosed ? "bg-slate-150 text-slate-400 line-through" : "bg-cyan-50 text-cyan-700")}>
        {names[s.dayOfWeek]}
      </span>
    ))
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Salas de Tratamiento</h1>
          <p className="text-xs text-slate-500">{rooms.length} salas disponibles para reservas</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 h-9 text-sm font-semibold rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-sm">
          <Plus className="h-4 w-4" /> Nueva sala
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por sala o especialidad..."
            className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 transition"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(room => (
            <div key={room.id} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 hover:shadow-md transition-all flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <h3 className="font-bold text-sm text-slate-800">{room.name}</h3>
                  </div>
                  <button onClick={() => toggleActive(room.id)}
                    className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full border transition", room.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-400 border-slate-200")}>
                    {room.isActive ? 'Activa' : 'Inactiva'}
                  </button>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="font-semibold text-slate-500 w-24">Especialidad:</span>
                    <span className="capitalize bg-cyan-50/60 px-2 py-0.5 rounded text-cyan-700 font-medium">{room.specialty}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="font-semibold text-slate-500 w-24">Capacidad:</span>
                    <span>{room.capacity} camilla(s) / cliente(s)</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Horario semanal:</span>
                    <div className="flex flex-wrap gap-0.5">{getWeekdaysShort(room)}</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" /> 09:00 - 20:00
                </span>
                <span className="font-medium text-cyan-600">Edición en Configuración</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNew(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4">Añadir Nueva Sala</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nombre de la Sala *</label>
                <input type="text" placeholder="Ej: Cabina Facial 2" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Especialidad Principal *</label>
                <select className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={specialty} onChange={e => setSpecialty(e.target.value)}>
                  <option value="facial">Facial</option>
                  <option value="masajes">Masajes</option>
                  <option value="uñas">Uñas</option>
                  <option value="depilación">Depilación</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Capacidad de Camillas/Boxes *</label>
                <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={capacity} onChange={e => setCapacity(Number(e.target.value))} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={handleCreate} disabled={!name} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Guardar Sala</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
